import { Chip, Link, Typography } from '@mui/material';
import type { GridInitialState } from '@mui/x-data-grid-premium';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import type { FC } from 'react';
import { useContext, useMemo } from 'react';
import { Link as RouterLink } from 'react-router';

import { UserContext } from '~/auth/userContext';
import { LiftAITable } from '~/components/table';
import type { LiftAIGridColDef } from '~/components/table/colDefTemplate';
import type { CarHistoryEvent } from '~/data/generated/model';
import { useDetailTableHeight } from '~/hooks/useDetailTableHeight';
import { formatCurrency } from '~/utils/format';

import {
  CAR_HISTORY_TYPE_CONFIG,
  CAR_HISTORY_TYPES,
  carHistorySourceLink,
  carHistoryStatusColor,
  formatCarHistoryDate,
} from './carHistorySource';

const initialState: GridInitialState = {
  sorting: { sortModel: [{ field: 'eventDate', sort: 'desc' }] },
};

const typeOptions = CAR_HISTORY_TYPES.map((type) => ({
  value: type,
  label: CAR_HISTORY_TYPE_CONFIG[type].label,
}));

interface AssetHistoryTableProps {
  events: CarHistoryEvent[];
  loading?: boolean;
}

const AssetHistoryTable: FC<AssetHistoryTableProps> = ({ events, loading = false }) => {
  const apiRef = useGridApiRef();
  const tableHeight = useDetailTableHeight();
  const { currencyCode } = useContext(UserContext);

  const columns = useMemo<LiftAIGridColDef<CarHistoryEvent>[]>(
    () => [
      {
        field: 'type',
        headerName: 'Type',
        type: 'singleSelect',
        width: 170,
        valueOptions: typeOptions,
        renderCell: ({ row }) => {
          const { icon: Icon, color, label } = CAR_HISTORY_TYPE_CONFIG[row.type];
          return (
            <Chip
              icon={<Icon sx={{ fontSize: 16 }} />}
              label={label}
              size="small"
              color={color}
              variant="outlined"
            />
          );
        },
      },
      {
        field: 'title',
        headerName: 'Name',
        flex: 2,
        minWidth: 200,
        renderCell: ({ row }) => {
          const href = carHistorySourceLink(row);
          return href ? (
            <Link component={RouterLink} to={href} variant="body2" underline="hover">
              {row.title}
            </Link>
          ) : (
            <Typography variant="body2">{row.title}</Typography>
          );
        },
      },
      {
        field: 'eventDate',
        headerName: 'Date',
        width: 140,
        renderCell: ({ row }) =>
          row.isCoverage ? (
            <Chip label="Coverage" size="small" variant="outlined" />
          ) : (
            <Typography variant="body2">{formatCarHistoryDate(row.eventDate)}</Typography>
          ),
      },
      {
        field: 'subtitle',
        headerName: 'Details',
        flex: 2,
        minWidth: 200,
        valueGetter: (value: string | null) => value ?? '—',
      },
      {
        field: 'amount',
        headerName: 'Amount',
        type: 'number',
        width: 130,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (value: string | null) => (value == null ? null : Number(value)),
        valueFormatter: (value: number | null) =>
          value == null ? '—' : formatCurrency({ value, currencyCode }),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        renderCell: ({ row }) =>
          row.status ? (
            <Chip
              label={row.status}
              size="small"
              color={carHistoryStatusColor(row.status)}
              variant="outlined"
              sx={{ textTransform: 'capitalize' }}
            />
          ) : null,
      },
    ],
    [currencyCode],
  );

  return (
    <LiftAITable<CarHistoryEvent>
      height={tableHeight}
      apiRef={apiRef}
      rows={events}
      columns={columns}
      getRowId={(row) => `${row.type}-${row.id}`}
      loading={loading}
      tableHeaderTitle="Asset History"
      enableQuickFilter
      quickFilterPlaceHolder="Search this asset’s history…"
      initialState={initialState}
      disableRowSelectionOnClick
      pageSizeOptions={[25, 50, 100]}
      paginationModel={{ pageSize: 25, page: 0 }}
    />
  );
};

export default AssetHistoryTable;
